import React, { useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import Form from '../../../../../components/form/Form';
import Panel from '../../../../../components/Panel';
import Select from '../../../../../components/input/Select';
import {
  WIDGET_TYPE_LABEL_V2,
  WIDGET_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/widgetConstantsV2';
import {
  PRODUCT_WIDGET_LAYOUT_TYPE_V2,
  ProductWidgetCreatePayloadV2,
  ProductWidgetDetailV2,
  WIDGET_LAYOUT_V2,
  WIDGET_TYPE_V2,
} from '../../../../../types/widget';
import Switch from '../../../../../components/input/Switch';
import WidgetLayoutSelect from '../../common/components/WidgetLayoutSelect/WidgetLayoutSelect';
import Input from '../../../../../components/input/Input';
import TextArea from '../../../../../components/input/TextArea';
import { InfiniteRangePicker } from '../../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../../types/common/date';
import ImageAndVideoUploadV2 from '../../../../../components/input/ImageAndVideoUploadV2';
import SelectList from '../../../../../components/input/SelectList';
import { PRODUCT_UPLOAD_TEMPLATE_XLSX_URL } from '../../../../../constants/service/common/staticFileUrlConstants';
import SimpleTableTransfer from '../../../../../components/input/SimpleTableTransfer';
import { productColumns, eventReactTableColumns } from '../../common/columns';
import { WIDGET_PRODUCT_RANDOM_ITEM_COUNT_SELECT_LIST_V2 } from '../../../../../constants/service/app/widgetConstantsV2';
import LinkItem from '../../../../../components/input/LinkItem';
import { useProductWidgetFormRules } from './useProductWidgetFormRules';
import SearchListTrigger from '../../../../../components/SearchListTrigger';
import moment from '../../../../../lib/moment';
import { eventListFetcher } from '../../common/fetchers/eventListFetcher';
import { productListFetcher } from '../../common/fetchers/productListFetcher';
import { useProductWidgetForm } from './useProductWidgetForm';
import { useProductListExcel } from '../../../../../hooks/product/useProductListExcel';
import Notice from '../../../../../components/Notice';
import { WIDGET } from '../../../../../constants/queryKeys';
import WidgetFormFooter from '../../WidgetFormFooter';
import ProductWidgetPreviewModal from './ProductWidgetPreviewModal/ProductWidgetPreviewModal';
import { ProductItemV2 } from '../../../../../types/product';
import toastMsg from '../../../../../lib/toastMsg';
import { EVENT_STATUS_V2 } from '../../../../../types/event';
import { EVENT_STATUS_LABEL_V2 } from '../../../../../constants/service/app/eventConstantsV2';
import { textNormalize } from '../../../../../lib/utils/form/normalize';

interface ProductWidgetFormProps {
  form: WrappedFormUtils;
  isEdit: boolean;
  initialValue: Partial<ProductWidgetDetailV2>;
  onChangeType: (type: WIDGET_TYPE_V2) => void;
  onFinish: (value: ProductWidgetCreatePayloadV2) => void;
}

const ProductWidgetForm = (props: ProductWidgetFormProps): JSX.Element => {
  const { isEdit, initialValue, form, onFinish, onChangeType } = props;
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const { widgetItemProducts, event, layoutType, setIsEventConnect, setLayoutType, setWidgetItemProducts } =
    useProductWidgetForm({ form, initialValue, isEdit });

  const randomCountSelectList =
    WIDGET_PRODUCT_RANDOM_ITEM_COUNT_SELECT_LIST_V2[layoutType || PRODUCT_WIDGET_LAYOUT_TYPE_V2.GRID_2X2] || [];

  const formRule = useProductWidgetFormRules({
    form,
    initialValue,
    isEdit,
  });

  const { uploadProductListExcel, downloadProductListExcel } = useProductListExcel({
    onUploadSuccess: (res) => {
      form.setFieldsValue({
        widgetItemProducts: res.products.map((item) => new ProductItemV2(item)),
        exposureWidgetItemProducts: [],
      });

      toastMsg.success('위젯 아이템 EXCEL 업로드하기 성공');
    },
  });

  const handleFinish = (value: ProductWidgetDetailV2): void => {
    onFinish(new ProductWidgetCreatePayloadV2(value));
  };

  return (
    <Form form={form} onFinish={handleFinish} scrollToFirstError>
      <Panel title="위젯 정보">
        <Panel.Section>
          <Form.Item
            name="type"
            label="위젯 타입"
            notice={isEdit ? <Notice>* 위젯 복사, 편집 시에는 위젯 데이터 타입을 변경할 수 없습니다.</Notice> : null}
          >
            {isEdit ? (
              <span>{WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.PRODUCT]}</span>
            ) : (
              <Select list={WIDGET_TYPE_SELECT_LIST_V2} onChange={onChangeType} style={{ width: 140 }} />
            )}
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="isProductRecommend"
            label="자동 추천 위젯 여부"
            valuePropName="checked"
            notice={
              <Notice>
                {isEdit
                  ? '* 위젯 복사, 편집 시에는 자동 추천 위젯 여부를 변경할 수 없습니다.'
                  : '* 자동 추천 위젯인 경우, 노출될 상품은 자동으로 설정됩니다.'}
              </Notice>
            }
          >
            <Switch disabled={formRule.isProductRecommend.disabled} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="isEventConnect"
            label="기획전 연결 여부"
            valuePropName="checked"
            notice={
              <Notice>
                <p>* 위젯 to 기획전 연결 시에 선택해주세요.</p>
                <p>* 연결 가능한 기획전 정보를 선택 할 수 있습니다.</p>
              </Notice>
            }
          >
            <Switch onChange={setIsEventConnect} disabled={formRule.isEventConnect.disabled} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="layoutType" label="위젯 레이아웃 타입">
            <WidgetLayoutSelect
              type={WIDGET_TYPE_V2.PRODUCT}
              onChange={(value: WIDGET_LAYOUT_V2): void => setLayoutType(value as PRODUCT_WIDGET_LAYOUT_TYPE_V2)}
            />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="mainTitle"
            label="위젯 타이틀"
            rules={formRule.mainTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="subTitle"
            label="위젯 서브 타이틀"
            rules={formRule.subTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 서브 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="memo" label="메모" maxCount={1000} rules={formRule.memo.rules} normalize={textNormalize}>
            <TextArea />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="exposurePeriod"
            label="위젯 노출기간"
            notice={<Notice>* 해당 위젯이 메인/서브 페이지에서 노출되는 기간입니다.</Notice>}
          >
            <InfiniteRangePicker format={DATE_FORMAT.FULL_DASH_FORMAT} showTime={true} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.buttonText.hideIf}>
          <Form.Item
            name="buttonText"
            label="버튼명"
            rules={formRule.buttonText.rules}
            normalize={textNormalize}
            notice={
              <Notice>
                <p>* 위젯 버튼명의 글자 수는 최대 10자입니다.</p>
                <p>* 위젯 버튼은 상품 위젯 타입에서만 사용 가능하며, 위젯 상세로 이동하는 버튼입니다.</p>

                <p>
                  * 위젯 버튼 설정 시, 해당 위젯의 상세페이지가 만들어지며,
                  <br /> 위젯에 속한 상품들의 상품 상세 내 대표이미지 마지막 장에서 “더 보기” 버튼을 통해 해당 위젯
                  상세로 이동할 수 있습니다.
                </p>
              </Notice>
            }
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.event.hideIf}>
          <Form.Item
            name="event"
            label="기획전 연결"
            required={true}
            rules={formRule.event.rules}
            notice={<Notice>* 위젯에 연결할 기획전을 선택해주세요</Notice>}
          >
            <LinkItem
              queryKey={WIDGET.EVENT_LIST}
              columns={eventReactTableColumns}
              fetcher={eventListFetcher}
              validateItem={(event): { message: string } | null =>
                event.status === EVENT_STATUS_V2.INACTIVE ? { message: '기획전 활성 여부를 확인해주세요' } : null
              }
              trigger={(openModal): JSX.Element => {
                const eventStatus = event?.status ? EVENT_STATUS_LABEL_V2[event?.status] : '-';
                const eventPeriod =
                  event?.status === EVENT_STATUS_V2.UNLIMITED
                    ? '무기한'
                    : `${moment(event?.startDate).format(DATE_FORMAT.FULL_DASH_FORMAT)} ~ ${moment(
                        event?.endDate,
                      ).format(DATE_FORMAT.FULL_DASH_FORMAT)}`;

                return (
                  <SearchListTrigger
                    value={event}
                    triggerText="+기획전 추가"
                    linkText={`${event?.mainTitle}(${event?.id})`}
                    onClick={openModal}
                    subText={`기획전 상태 : ${eventStatus} | 기획전 기간 : ${eventPeriod}`}
                  />
                );
              }}
            />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="노출되고 있는 페이지">
            <span>{initialValue?.currentExposurePage || '-'}</span>
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="위젯 상세">
        <Panel.Section hideIf={formRule.detailImages.hideIf}>
          <Form.Item
            name="detailImages"
            label="위젯 상세 화면 상단 이미지"
            maxCount={100}
            rules={formRule.detailImages.rules}
            notice={
              <Notice>
                <p>* 위젯 상세 상단에 노출되는 이미지입니다.</p>
                <p>* 위젯 버튼명을 사용하는 경우에만 위젯 상세 상단 이미지를 설정할 수 있습니다.</p>
              </Notice>
            }
          >
            <ImageAndVideoUploadV2 modalTitle="영상 업로드" canAddVideo={true} multiple={true} maxCnt={100} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.widgetItemProducts.hideIf}>
          <Form.Item
            name="widgetItemProducts"
            label="위젯 아이템 선택"
            layoutType="vertical"
            rules={formRule.widgetItemProducts.rules}
            notice={<Notice>* 선택된 아이템은 회색으로 보이게 되며, 선택이 되지 않습니다.</Notice>}
          >
            <SelectList
              onChange={setWidgetItemProducts}
              excelTemplateUrl={PRODUCT_UPLOAD_TEMPLATE_XLSX_URL}
              queryKey={WIDGET.PRODUCT_LIST}
              fetcher={productListFetcher}
              columns={productColumns}
              onExcelUpload={uploadProductListExcel}
              onDownloadExcel={downloadProductListExcel}
              initialOption={{ pagination: { pageNum: 0, pageSize: 20 } }}
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="홈에서 상품 위젯 노출 정보를 결정하는 옵션">
        <Panel.Section hideIf={formRule.topImage.hideIf}>
          <Form.Item
            name="topImage"
            label="위젯 홈 상단 이미지"
            notice={<Notice>* 위젯 상단에 노출되는 이미지입니다.</Notice>}
          >
            <ImageAndVideoUploadV2 modalTitle="이미지 업로드" multiple={false} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.isRandomItem.hideIf}>
          <Form.Item
            name="isRandomItem"
            label="위젯 아이템 랜덤 노출 여부"
            valuePropName="checked"
            notice={
              <Notice>
                <p>* 위젯 아이템을 랜덤으로 노출하지 않을 경우에는, 홈에 설정된 위젯 아이템 순서대로 노출이 됩니다.</p>
                <p>* 위젯을 저장하기 전에 꼭 미리보기를 확인하시기 바랍니다.</p>
              </Notice>
            }
          >
            <Switch />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.exposureWidgetItemProducts.hideIf}>
          <Form.Item
            name="exposureWidgetItemProducts"
            layoutType="vertical"
            rules={formRule.exposureWidgetItemProducts.rules}
          >
            <SimpleTableTransfer
              dataSource={widgetItemProducts || []}
              leftTitle="위젯 상세에 설정된 위젯 아이템 리스트"
              rightTitle="홈에서 설정된 위젯 아이템 리스트"
              leftColumns={productColumns}
              rightColumns={productColumns}
            />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={formRule.exposureItemCount.hideIf}>
          <Form.Item
            name="exposureItemCount"
            label="노출할 위젯 아이템 개수"
            notice={<Notice>* 위젯에 노출 된 상품은 자동으로 설정되며, 설정한 개수에 맞게 노출 됩니다.</Notice>}
            initialValue={randomCountSelectList[0].id}
          >
            <Select list={randomCountSelectList} style={{ width: 140 }} />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Form.Item>
        <WidgetFormFooter
          typeLabel={WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.PRODUCT]}
          onClickPreview={(): void => setIsPreviewModalOpen(true)}
        />
      </Form.Item>

      {isPreviewModalOpen && (
        <ProductWidgetPreviewModal
          detail={form.getFieldsValue() as ProductWidgetDetailV2}
          onClose={(): void => setIsPreviewModalOpen(false)}
        />
      )}
    </Form>
  );
};

export default Form.create<ProductWidgetFormProps>()(ProductWidgetForm);
